import type { ReactNode } from 'react';
import { create } from 'zustand';

import { closeOtherDrawers, registerDrawerCloser } from './_drawer-mutex';
import { assertMutexHelperContext } from './_panel-mutex-flag';
import { closeOtherRightSlotSurfaces, registerRightSlotCloser } from './_right-slot-mutex';

import type { FormatId } from '@/lib/ai-agent/prompts/format-fragments';
import type { CampaignFlow } from '@/lib/ai-agent/schemas/campaign-flow';
import type { WorkbenchPostUpdate } from '@/lib/create/workbench/types';
import { registerWorkspaceReset } from '@/lib/workspace/workspaceResetRegistry';

export type ArtifactType =
  | 'plan'
  | 'strategy'
  | 'calendar'
  | 'checklist'
  | 'brief'
  | 'post-draft'
  | 'repurpose-workbench'
  | 'video-studio'
  | 'script'
  | 'blog'
  | 'article'
  | 'brand-guidelines'
  | 'campaign-flow'
  /**
   * A rendered FILE, not a document. Every other type here is prose the rail
   * renders from markdown; this one is bytes we already generated — a real PDF
   * behind a blob URL.
   *
   * It exists because the chat card's file rows are export ACTIONS wearing file
   * names: three rows labelled `.pdf`, `.docx` and `.md` all opened the same
   * markdown source, so the label and the result disagreed. A row can only
   * honestly open something we can actually render as that type.
   */
  | 'file-preview'
  | 'other';

export type ArtifactStatus = 'opening' | 'streaming' | 'ready' | 'saving' | 'saved' | 'error';

export type VideoStudioEntryKind = 'url' | 'image' | 'freeform';

export interface ArtifactRef {
  id: string;
  messageId: string;
  toolCallId: string;
  title: string;
  type: ArtifactType;
  planId?: string | null;
  collectionId?: string | null;
  savedFileId?: string | null;
  articleId?: string | null;
  jobId?: string | null;
  entryKind?: VideoStudioEntryKind;
  initialPrompt?: string;
  sourceUrl?: string;
  /**
   * `file-preview` only: an object URL for bytes generated in this tab, plus
   * the name to show and the type to render as. Deliberately NOT persisted or
   * resumed — an object URL is dead the moment the tab that minted it is gone,
   * so a `file-preview` is a live-session artifact by construction rather than
   * something a restored thread can leave pointing at nothing.
   */
  fileUrl?: string;
  fileName?: string;
  fileMime?: string;
  status: ArtifactStatus;
}

export type GenerateEntryHandler = (
  entryId: string,
  entryMeta?: { topic?: string | null; dayOfWeek?: number | null; weekOffset?: number | null },
) => Promise<void>;
export type RegenerateHandler = () => Promise<void> | void;

/**
 * Persistent base of the drawer body. Workspace = plan workspace canvas.
 * Workflow = active workflow tracker (auto-opens on approval). Activity =
 * compact tool-activity rail for an explicit future/user-invoked entry point.
 * Multi-tool turns stay inline and never auto-open this rail.
 *
 * Documents are an OVERLAY on top of the base (see `documentStack`) — they
 * don't replace the base, so popping a document returns the user to whatever
 * base context was active.
 */
export type BaseMode = 'workspace' | 'workflow' | 'activity';

/**
 * Derived from store state — never stored. Includes 'closed' (drawer not
 * open), 'document' (stack non-empty), or one of the three base modes.
 */
export type VisibleMode = 'closed' | BaseMode | 'document';

export type SendMessageFn = (arg: {
  text: string;
  body?: Record<string, unknown>;
}) => Promise<unknown>;

export type WorkbenchDispatchFn = (
  toolCallId: string,
  formatHint: FormatId,
  patch: WorkbenchPostUpdate,
) => void;

export interface ArticleHydrateInput {
  id: string;
  title: string;
  domain: string;
  sourceUrl: string;
}
export type WorkbenchHydrateFn = (article: ArticleHydrateInput) => void;

/**
 * Cross-tree bridge for the in-pane post-focus body. CreateChatPage owns the
 * generated-post array + per-post action handlers (approve, schedule, edit,
 * etc.) but the drawer is mounted in AppLayout. The page registers a
 * `(postId) => ReactNode` renderer; the drawer reads it when the
 * `documentStack.top()?.type === 'post-draft'` and renders the result inside
 * the drawer body. Same pattern as `sendMessage` / `workbenchDispatch`.
 */
export type PostFocusRenderer = (postId: string) => ReactNode | null;

const DRAWER_STORAGE_KEY = 'mora.workspace.open';

function writeDrawerOpen(open: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(DRAWER_STORAGE_KEY, open ? 'true' : 'false');
  } catch {
    /* noop */
  }
}

interface ArtifactState {
  /**
   * @deprecated Use `documentStack` + `pushDocument(ref)` instead. The legacy
   * `active` slot is preserved through the v1.3 milestone for the four legacy
   * canvases (PlanStrategyCanvas, BrandGuidelinesCanvas, MarkdownCanvas,
   * VideoStudioCanvas) that still consume it via `useArtifactStore.active`.
   * It will be removed in a follow-up cleanup phase.
   */
  active: ArtifactRef | null;
  isDrawerOpen: boolean;
  setDrawerOpen: (open: boolean) => void;
  toggleDrawer: () => void;
  /**
   * Persistent base of the drawer body. Workspace by default; switched to
   * 'workflow' when an approval is requested or a workflow auto-triggers, and
   * 'activity' when a multi-tool turn fires. Persists across document layers
   * — popping the document stack returns the user here.
   */
  baseMode: BaseMode;
  /**
   * Stack of document overlays the user focused (file-row clicks, post-chip
   * clicks, etc.). Last entry is the visible document; popping returns to
   * the previous one, and popping the last entry returns to the base mode.
   */
  documentStack: ArtifactRef[];
  /** Most recent multi-tool turn that opened activity rail (lifecycle marker). */
  lastActivityTurnId: string | null;
  /** Turn ids the user explicitly dismissed activity for; auto-open suppressed. */
  activityDismissedTurnIds: Set<string>;
  /**
   * Per-artifact scroll offsets (top-px). Saved as the user scrolls in a
   * document body; restored when the same artifact id is re-rendered. Cleared
   * via `clearScrollOffset` when an artifact is removed. P5 §3.5 / §21.12.
   */
  scrollOffsets: Map<string, number>;
  setScrollOffset: (artifactId: string, offset: number) => void;
  getScrollOffset: (artifactId: string) => number;
  clearScrollOffset: (artifactId: string) => void;
  pushDocument: (ref: ArtifactRef) => void;
  popDocument: () => void;
  /**
   * P1.6 — bring an existing stacked document to the top (tab switch). No-op
   * if no doc with that id is in the stack. Keyed on `id`, not `toolCallId`
   * — see the dedup note on `pushDocument`.
   */
  focusDocument: (id: string) => void;
  /**
   * P1.6 — remove a doc from the stack regardless of position (tab close).
   * Drops drawer-open if the stack drains and base mode is workspace. Keyed
   * on `id`, not `toolCallId` — see the dedup note on `pushDocument`.
   */
  closeDocument: (id: string) => void;
  resetDrawer: () => void;
  /**
   * Workflow / activity helpers. All idempotent; all open the drawer if
   * closed; all register with `closeOtherDrawers('artifact')` mutex first so
   * the right rail swaps content rather than stacking.
   */
  openWorkflowMode: (opts?: { reason?: 'approval' | 'auto-trigger' | 'user-click' }) => void;
  openActivityMode: (opts: {
    turnId: string;
    reason?: 'multi-tool' | 'single-approval' | 'user-click';
  }) => void;
  handleWorkflowEnd: () => void;
  dismissActivityForTurn: (turnId: string) => void;
  /** Live agent messages synced from CreateChatPage so a hoisted canvas can read part streams. */
  agentMessages: Array<{ id: string; parts?: Array<Record<string, unknown>> }>;
  /** Plan context for the canvas save flow (planId, collectionId, name). */
  planContext: { planId?: string | null; collectionId?: string | null; planName?: string | null };
  _planContextHash: string | null;
  /**
   * The CURRENT home-composer chat session id, mirrored from CreateChatPage.
   * Used by `handleSaveStrategy` (ArtifactCanvasContainer) to stamp the
   * founding conversation with the just-created project's id — without this the
   * conversation that CREATED the plan is orphaned (never appears in the
   * project). Null when no chat session is active.
   */
  activeSessionId: string | null;
  setActiveSessionId: (id: string | null) => void;
  /**
   * The project a chat session is bound to at the persisted-binding layer
   * (`chat_sessions.project_id` / `ai_sessions.project_id`), mirrored from
   * CreateChatPage's `assignedProjectId`. That value is display-only for the
   * title menu elsewhere, but `handleSaveStrategy` needs it too: a chat
   * opened via `?resumeSession=` (no `?planId=` in the URL) has this as the
   * ONLY signal that the session is already bound to a project — without it,
   * Save forks a new project instead of updating the bound one.
   */
  assignedProjectId: string | null;
  setAssignedProjectId: (id: string | null) => void;
  handleGenerateEntry?: GenerateEntryHandler;
  regenerate?: RegenerateHandler;
  sendMessage?: SendMessageFn;
  workbenchDispatch?: WorkbenchDispatchFn;
  workbenchHydrate?: WorkbenchHydrateFn;
  postFocusRenderer?: PostFocusRenderer;
  dbStrategy: { planId: string; strategy: unknown } | null;
  setDbStrategy: (strategy: { planId: string; strategy: unknown } | null) => void;
  /** Captured campaign-flow payloads keyed by toolCallId. The storyboard renders
   * from this (set when the card opens) so it survives the live message thread
   * changing/blanking — mirrors the dbStrategy fallback for plans. */
  capturedCampaignFlows: Record<string, CampaignFlow>;
  setCapturedCampaignFlow: (toolCallId: string, flow: CampaignFlow) => void;
  /** @deprecated Use `pushDocument(ref)` — see `active` field jsdoc. */
  open: (ref: ArtifactRef) => void;
  /** @deprecated Use `popDocument()` — see `active` field jsdoc. */
  close: () => void;
  /** @deprecated Update the ref on the document stack instead of mutating `active`. */
  setStatus: (status: ArtifactStatus) => void;
  /** @deprecated Update the ref on the document stack instead of mutating `active`. */
  markSaved: (savedFileId: string) => void;
  setAgentMessages: (
    messages: Array<{ id: string; parts?: Array<Record<string, unknown>> }>,
  ) => void;
  setPlanContext: (
    ctx: {
      planId?: string | null;
      collectionId?: string | null;
      planName?: string | null;
    } | null,
  ) => void;
  setHandleGenerateEntry: (fn: GenerateEntryHandler | undefined) => void;
  setRegenerate: (fn: RegenerateHandler | undefined) => void;
  setSendMessage: (fn: SendMessageFn | undefined) => void;
  setWorkbenchDispatch: (fn: WorkbenchDispatchFn | undefined) => void;
  setWorkbenchHydrate: (fn: WorkbenchHydrateFn | undefined) => void;
  setPostFocusRenderer: (fn: PostFocusRenderer | undefined) => void;
}

/**
 * Pure selector — derives the visible drawer surface from store state.
 * Never stored. Order: closed → document overlay → base mode.
 */
export function selectVisibleMode(s: ArtifactState): VisibleMode {
  if (!s.isDrawerOpen) return 'closed';
  if (s.documentStack.length > 0) return 'document';
  return s.baseMode;
}

/**
 * Module-scoped flag so the legacy-`open` deprecation warning fires at most
 * once per session. Reset only on full module reload (not store reset), which
 * matches user intent — devs should be reminded once, not every interaction.
 * Exported only for tests so they can clear it between cases.
 */
let _activeOpenWarned = false;
export function _resetActiveOpenWarnedForTests(): void {
  _activeOpenWarned = false;
}

export const useArtifactStore = create<ArtifactState>((set, get) => ({
  active: null,
  isDrawerOpen: false,
  baseMode: 'workspace',
  documentStack: [],
  lastActivityTurnId: null,
  activityDismissedTurnIds: new Set<string>(),
  scrollOffsets: new Map<string, number>(),
  setScrollOffset: (artifactId, offset) =>
    set((state) => {
      const prev = state.scrollOffsets.get(artifactId);
      if (prev === offset) return state;
      const next = new Map(state.scrollOffsets);
      next.set(artifactId, offset);
      return { scrollOffsets: next };
    }),
  getScrollOffset: (artifactId) => get().scrollOffsets.get(artifactId) ?? 0,
  clearScrollOffset: (artifactId) =>
    set((state) => {
      if (!state.scrollOffsets.has(artifactId)) return state;
      const next = new Map(state.scrollOffsets);
      next.delete(artifactId);
      return { scrollOffsets: next };
    }),
  setDrawerOpen: (open) => {
    if (open) {
      assertMutexHelperContext();
      closeOtherRightSlotSurfaces('artifact-drawer');
    }
    writeDrawerOpen(open);
    set(() => {
      if (!open) {
        return { isDrawerOpen: false };
      }
      return { isDrawerOpen: true };
    });
  },
  toggleDrawer: () =>
    set((state) => {
      const next = !state.isDrawerOpen;
      if (next) closeOtherRightSlotSurfaces('artifact-drawer');
      writeDrawerOpen(next);
      return { isDrawerOpen: next };
    }),
  pushDocument: (ref) => {
    closeOtherRightSlotSurfaces('artifact-drawer');
    closeOtherDrawers('artifact');
    writeDrawerOpen(true);
    set((state) => {
      // Dedupe on `id`, not `toolCallId` (caught live 2026-08-09 — a
      // document showed as two identical tabs in DrawerTabStrip). `id`
      // resolves to the real persisted output.id once generation completes
      // (see GenerateDocumentPart's `resolvedId`); `toolCallId` is only a
      // per-call handle, so the SAME underlying document referenced from a
      // second tool call (a retry, a re-open, a resumed workflow step) had
      // a different toolCallId and was wrongly treated as a new document.
      const existingIndex = state.documentStack.findIndex((r) => r.id === ref.id);
      if (existingIndex >= 0) {
        const next = [
          ...state.documentStack.slice(0, existingIndex),
          ...state.documentStack.slice(existingIndex + 1),
          ref,
        ];
        return { isDrawerOpen: true, documentStack: next };
      }
      return {
        isDrawerOpen: true,
        documentStack: [...state.documentStack, ref],
      };
    });
  },
  popDocument: () =>
    set((state) => {
      if (state.documentStack.length === 0) return state;
      const next = state.documentStack.slice(0, -1);
      if (next.length === 0 && state.baseMode === 'workspace') {
        writeDrawerOpen(false);
        return { documentStack: next, isDrawerOpen: false };
      }
      return { documentStack: next };
    }),
  focusDocument: (id) =>
    set((state) => {
      const idx = state.documentStack.findIndex((r) => r.id === id);
      if (idx === -1) return state;
      if (idx === state.documentStack.length - 1) return state;
      const ref = state.documentStack[idx];
      const next = [
        ...state.documentStack.slice(0, idx),
        ...state.documentStack.slice(idx + 1),
        ref,
      ];
      return { documentStack: next };
    }),
  closeDocument: (id) =>
    set((state) => {
      const idx = state.documentStack.findIndex((r) => r.id === id);
      if (idx === -1) return state;
      const next = [...state.documentStack.slice(0, idx), ...state.documentStack.slice(idx + 1)];
      if (next.length === 0 && state.baseMode === 'workspace') {
        writeDrawerOpen(false);
        return { documentStack: next, isDrawerOpen: false };
      }
      return { documentStack: next };
    }),
  resetDrawer: () => {
    set((state) => ({
      baseMode: 'workspace' as BaseMode,
      documentStack: [],
      isDrawerOpen: state.isDrawerOpen,
    }));
  },
  openWorkflowMode: (_opts) => {
    const state = get();
    if (state.isDrawerOpen && state.baseMode === 'workflow') {
      return;
    }
    closeOtherRightSlotSurfaces('artifact-drawer');
    closeOtherDrawers('artifact');
    writeDrawerOpen(true);
    set({ isDrawerOpen: true, baseMode: 'workflow' });
  },
  openActivityMode: (opts) => {
    const state = get();
    if (state.activityDismissedTurnIds.has(opts.turnId)) return;
    if (state.isDrawerOpen && state.baseMode === 'activity') {
      if (state.lastActivityTurnId !== opts.turnId) {
        set({ lastActivityTurnId: opts.turnId });
      }
      return;
    }
    closeOtherRightSlotSurfaces('artifact-drawer');
    closeOtherDrawers('artifact');
    writeDrawerOpen(true);
    set({
      isDrawerOpen: true,
      baseMode: 'activity',
      lastActivityTurnId: opts.turnId,
    });
  },
  handleWorkflowEnd: () => {
    set((state) => {
      if (state.baseMode !== 'workflow') return state;
      return { baseMode: 'workspace' as BaseMode };
    });
  },
  dismissActivityForTurn: (turnId) => {
    set((state) => {
      if (state.activityDismissedTurnIds.has(turnId)) return state;
      const nextSet = new Set(state.activityDismissedTurnIds);
      nextSet.add(turnId);
      return { activityDismissedTurnIds: nextSet };
    });
  },
  agentMessages: [],
  planContext: {},
  _planContextHash: null,
  activeSessionId: null,
  setActiveSessionId: (activeSessionId) =>
    set((state) => (state.activeSessionId === activeSessionId ? state : { activeSessionId })),
  assignedProjectId: null,
  setAssignedProjectId: (assignedProjectId) =>
    set((state) => (state.assignedProjectId === assignedProjectId ? state : { assignedProjectId })),
  handleGenerateEntry: undefined,
  setAgentMessages: (agentMessages) => set({ agentMessages }),
  setPlanContext: (ctx) =>
    set((state) => {
      const hash = ctx
        ? `${ctx.planId ?? ''}|${ctx.planName ?? ''}|${ctx.collectionId ?? ''}`
        : null;
      if (state._planContextHash === hash) return state;
      return {
        planContext: ctx ?? {},
        _planContextHash: hash,
      };
    }),
  setHandleGenerateEntry: (handleGenerateEntry) => set({ handleGenerateEntry }),
  setRegenerate: (regenerate) => set({ regenerate }),
  sendMessage: undefined,
  setSendMessage: (sendMessage) => set({ sendMessage }),
  workbenchDispatch: undefined,
  setWorkbenchDispatch: (workbenchDispatch) => set({ workbenchDispatch }),
  workbenchHydrate: undefined,
  setWorkbenchHydrate: (workbenchHydrate) => set({ workbenchHydrate }),
  postFocusRenderer: undefined,
  setPostFocusRenderer: (postFocusRenderer) => set({ postFocusRenderer }),
  dbStrategy: null,
  setDbStrategy: (dbStrategy) => set({ dbStrategy }),
  capturedCampaignFlows: {},
  setCapturedCampaignFlow: (toolCallId, flow) =>
    set((s) => ({ capturedCampaignFlows: { ...s.capturedCampaignFlows, [toolCallId]: flow } })),
  open: (ref) => {
    if (import.meta.env.DEV && !_activeOpenWarned) {
      _activeOpenWarned = true;
      // eslint-disable-next-line no-console
      console.warn(
        '[useArtifactStore.open] Legacy `active` slot is deprecated. Use pushDocument(ref) instead. This warning fires once per session.',
      );
    }
    closeOtherRightSlotSurfaces('artifact-drawer');
    closeOtherDrawers('artifact');
    set((state) => {
      if (state.active && state.active.toolCallId === ref.toolCallId) {
        const titleChanged = ref.title !== state.active.title;
        const typeChanged = ref.type !== state.active.type;
        const statusChanged = ref.status !== state.active.status;
        if (!titleChanged && !typeChanged && !statusChanged) {
          return state;
        }
        return {
          active: {
            ...state.active,
            ...(titleChanged ? { title: ref.title } : {}),
            ...(typeChanged ? { type: ref.type } : {}),
            ...(statusChanged ? { status: ref.status } : {}),
          },
        };
      }
      return { active: ref };
    });
  },
  // Clears the legacy `active` slot ONLY. This is also the fn registered with
  // the drawer mutex, so it runs whenever ANY other dialog store opens (image,
  // document, video, strategy). It used to also reset baseMode to 'workspace',
  // which meant opening a generated image or script mid-workflow silently
  // destroyed the workflow base: the rail came back as the flat activity/
  // workspace surface instead of the step tracker the user had been working in
  // — "the right panel showed multiple different views… then when finished
  // totally converted to a different design" (2026-07-26).
  //
  // baseMode is the PERSISTENT context the whole overlay model rests on
  // (documents stack ON TOP of it and popping returns to it). Only the explicit
  // owners may change it: handleWorkflowEnd, openWorkflowMode/openActivityMode,
  // and resetDrawer.
  close: () => set({ active: null }),
  setStatus: (status) =>
    set((state) => (state.active ? { active: { ...state.active, status } } : state)),
  markSaved: (savedFileId) =>
    set((state) =>
      state.active ? { active: { ...state.active, status: 'saved', savedFileId } } : state,
    ),
}));

registerDrawerCloser('artifact', () => useArtifactStore.getState().close());
registerRightSlotCloser('artifact-drawer', () => {
  const store = useArtifactStore.getState();
  store.setDrawerOpen(false);
  store.resetDrawer();
});

if (typeof window !== 'undefined' && import.meta.env.DEV) {
  (window as unknown as { __artifactStore: typeof useArtifactStore }).__artifactStore =
    useArtifactStore;
}

// The right-rail artifact stack holds generated content belonging to ONE
// workspace, and it lives outside the React Query cache — so a workspace
// switch clearing that cache leaves the previous workspace's artifacts on
// screen. Registering here (rather than listing this store inside
// switchWorkspace) keeps the obligation next to the state that has it; see
// src/lib/workspace/workspaceResetRegistry.ts.
registerWorkspaceReset('artifact-store', () => {
  useArtifactStore.getState().resetDrawer();
});
